import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  DocumentsList,
  FormFieldSet,
  Modal,
  PageHeader,
  PageTitle,
  useFormHandler,
  TableAdv,
  SmartTooltip,
  showToast,
  Backdrop,
  LogoLoader,
} from "../../../../components";
import { mapTableRows } from "../../../../utils";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  addNewJournalEntry,
  fetchOneJournalEntry,
  updateJournalEntry,
} from "../../../../redux/slices/journalEntries/journalEntriesApi";
import { useLoginUser } from "../../../../hooks/useLoginUser";
import { useDispatch } from "react-redux";
import { uploadStart } from "../../../../redux/slices/universal/fileUploadSlice";
import { status } from "../../../../assets/data/projectData";

const getInitialFormData = (mode, editData) => {
  if (mode === "edit" && editData) {
    return {
      entries: [
        {
          id: editData?.id || uuidv4(),
          date: editData?.date || "",
          journalNo: editData?.uiid || editData?.journalId || editData?.journalNo || "",
          description:
            editData?.description || editData?.transactionDetails || "",
          debit: editData?.debit || "",
          credit: editData?.credit || "",
          attachments: editData?.attachments || [],
          employee: editData?.employee || editData?.employeeId || "",
          exchangeRate: editData?.exchangeRate || "",
          dollarAmount: editData?.dollarAmount || "",
        },
      ],
    };
  }

  // CREATE MODE
  return {
    entries: [
      {
        id: uuidv4(),
        date: "",
        journalNo: "",
        description: "",
        debit: "",
        credit: "",
        attachments: [],
        employee: "",
        exchangeRate: "",
        dollarAmount: "",
      },
      {
        id: uuidv4(),
        date: "",
        journalNo: "",
        description: "",
        debit: "",
        credit: "",
        attachments: [],
        employee: "",
        exchangeRate: "",
        dollarAmount: "",
      },
    ],
  };
};

const JournalEntry = () => {
  const navigate = useNavigate();
  const { journalId } = useParams();
  const { state } = useLocation();
  const dispatch = useDispatch();

  const mode = journalId ? "edit" : "create";
  const editData = state?.data ?? null;
  const onBack = () => navigate("/off-trx");

  const [initialData, setInitialData] = useState(() =>
    getInitialFormData(mode, editData),
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attachmentModal, setAttachmentModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;

  const { formData, handleChange, setFormData } = useFormHandler(initialData, {
    // journalNo: { type: "text", required: true },
    // date: { type: "date", required: true },
    // entries: [
    //   {
    //     date: { type: "date", required: true },
    //     description: { type: "text", required: true },
    //     debit: { type: "text" },
    //     credit: { type: "text" },
    //   },
    // ],
  });

  const addRow = () => {
    const newRow = {
      id: uuidv4(),
      date: "",
      journalNo: "",
      description: "",
      debit: "",
      credit: "",
      attachments: [],
      employee: "",
      exchangeRate: "",
      dollarAmount: "",
    };
    setFormData({ ...formData, entries: [...formData?.entries, newRow] });
  };

  const removeRow = (id) => {
    setFormData({
      ...formData,
      entries: formData?.entries?.filter((row) => row?.id !== id),
    });
  };

  const duplicateRow = (index) => {
    const currentRow = formData?.entries[index];
    const duplicatedRow = { ...currentRow, id: uuidv4() };
    const updatedEntries = [...formData?.entries];
    updatedEntries.splice(index + 1, 0, duplicatedRow);
    setFormData({ ...formData, entries: updatedEntries });
  };

  const handleReset = () => {
    setFormData(initialData);
  };

  useEffect(() => {
    const loadEditEntry = async () => {
      if (mode !== "edit" || editData || !journalId) return;

      setLoading(true);
      try {
        const response = await fetchOneJournalEntry({
          JournalEntryUiid: journalId,
          retrieve: "",
          ref: "",
          incl: "",
        });

        if (!response?.success) {
          showToast(
            response?.error || response?.msg || "Unable to load journal entry",
            "error",
            3000,
          );
          return;
        }

        const journalResponse = response?.data;
        const entry = Array.isArray(journalResponse)
          ? journalResponse[0]
          : journalResponse;

        if (entry) {
          const loadedInitialData = getInitialFormData("edit", {
            id: entry._id || entry.uiid || journalId,
            date: entry.date || "",
            journalId: entry.uiid || entry.journalId || entry.journalNo || "",
            description: entry.description || entry.transactionDetails || "",
            debit: entry.debit || "",
            credit: entry.credit || "",
            attachments: entry.attachments || [],
            employee: entry.employee || entry.employeeId || "",
            exchangeRate: entry.exchangeRate || "",
            dollarAmount: entry.dollarAmount || "",
          });
          setInitialData(loadedInitialData);
          setFormData(loadedInitialData);
        }
      } catch (error) {
        showToast(
          error?.error || error?.message || "Unable to load journal entry",
          "error",
          3000,
        );
      } finally {
        setLoading(false);
      }
    };

    loadEditEntry();
  }, [editData, journalId, mode, setFormData]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      setLoading(true);
      // First, handle file uploads for all entries with attachments
      const uploadPromises = formData.entries.map(async (entry, index) => {
        if (entry.attachments && entry.attachments.length > 0) {
          const newFiles = entry.attachments.filter(a => a instanceof File);
          if (newFiles.length > 0) {
            return new Promise((resolve) => {
              dispatch(
                uploadStart({
                  uploadType: "multiple",
                  field: "doc",
                  uiid: `temp_${index}`, // Temporary UIID for upload
                  entity: "journalentrys",
                  category: "journalentrys",
                  coll: "JournalEntry",
                  doc: newFiles,
                  addedBy: loginUserUiid,
                  onSuccess: (fileData) => {
                    const attachmentKeys = fileData.map((file) => file.key);
                    resolve(attachmentKeys);
                  },
                  onError: () => resolve([]), // Return empty array on error
                }),
              );
            });
          }
        }
        return []; // No attachments or no new files
      });

      const uploadResults = await Promise.all(uploadPromises);

      // Now create payload with attachment keys
      const payload = formData.entries.map((entry, index) => {
        const existingAttachments = entry.attachments.filter(a => typeof a === 'string');
        const newKeys = uploadResults[index] || [];
        const allAttachments = [...existingAttachments, ...newKeys];

        return {
          date: entry.date,
          ...(mode === "edit" && { status: 0 }),
          description: entry.description,
          debit: entry.debit,
          credit: entry.credit,
          attachments: allAttachments,
          employeeId: entry?.employee?.value || entry.employee || "",
          exchangeRate: entry.exchangeRate,
          dollarAmount: entry.dollarAmount,
          addedBy: loginUserUiid,
          updatedBy: loginUserUiid,
        };
      });

      let responses;
      if (mode === "create") {
        // Make separate API calls for each entry
        const promises = payload.map((entry) => addNewJournalEntry(entry));
        responses = await Promise.all(promises);
      } else {
        responses = [
          await updateJournalEntry({
            coll: "JournalEntry",
            uiid: journalId,
            payload: [{ type: "SET", value: payload?.[0] }],
          }),
        ];
      }

      // Check if all responses are successful
      const allSuccessful = responses.every((response) => response?.success);
      const failedCount = responses.filter((response) => !response?.success).length;

      if (allSuccessful) {
        showToast(
          mode === "create"
            ? `${payload.length} journal entries created successfully`
            : "Journal entry updated successfully",
          "success",
          3000,
        );
        handleReset();
        onBack?.();
      } else {
        showToast(
          mode === "create"
            ? `${failedCount} out of ${payload.length} journal entries failed to create`
            : "Unable to update journal entry",
          "error",
          3000,
        );
      }
    } catch (error) {
      showToast(
        error?.error || error?.message || "Unable to save journal entry",
        "error",
        3000,
      );
    } finally {
      setSubmitting(false);
      setLoading(true);
    }
  };
  const employeeOptions = [
    { label: "Raghu", value: "raghu" },
    { label: "Aishwarya Vedula", value: "Aishwarya Vedula" },
    { label: "Abdul Waseem Syed", value: "Abdul Waseem Syed" },
    { label: "Amol Dasani", value: "Amol Dasani" },
  ];

  const existingAttachments = formData?.entries?.[selectedIndex]?.attachments?.filter(a => typeof a === 'string') || [];
  const newAttachments = formData?.entries?.[selectedIndex]?.attachments?.filter(a => a instanceof File) || [];

  const existingAttachmentDocs = existingAttachments.map((attachment) => ({
    name: attachment?.split("/").pop() || attachment,
    filePath: attachment,
  }));

  const handleRemoveAttachment = (index, isNew) => {
    const currentAttachments = formData.entries[selectedIndex].attachments;
    if (isNew) {
      // Remove from new attachments
      const updated = currentAttachments.filter(a => !(a instanceof File) || currentAttachments.indexOf(a) !== index);
      handleChange(`entries[${selectedIndex}].attachments`, updated);
    } else {
      // For existing, remove the string
      const updated = currentAttachments.filter(a => typeof a !== 'string' || currentAttachments.indexOf(a) !== index);
      handleChange(`entries[${selectedIndex}].attachments`, updated);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "journalNo",
        label: "Journal No",
        render: (row) => (
            <FormFieldSet
              noTitle
              type="text"
              placeholder="Journal No"
              value={row?.journalNo === "-" ? "" : row?.journalNo}
              onChange={(value) =>
                handleChange(`entries[${row?._index}].journalNo`, value)
              }
            />
        ),
      },
      {
        key: "date",
        label: "Date",
        render: (row) => (
          <FormFieldSet
            noTitle
            type="datepicker"
            value={row?.date}
            onChange={(value) =>
              handleChange(`entries[${row?._index}].date`, value)
            }
            placeholder="Select Date"
            showDropdown={false}
          />
        ),
      },
      {
        key: "description",
        label: "Description",
        render: (row) => (
            <FormFieldSet
              noTitle
              type="text"
              value={row?.description === "-" ? "" : row?.description}
              onChange={(value) =>
                handleChange(`entries[${row?._index}].description`, value)
              }
              placeholder="Description"
            />
        ),
      },
      {
        key: "debit",
        label: "Debit",
        render: (row) => (
            <FormFieldSet
              noTitle
              type="text"
              value={row?.debit === "-" ? "" : row?.debit}
              onChange={(value) =>
                handleChange(`entries[${row?._index}].debit`, value)
              }
              placeholder="Debit"
            />
        ),
      },
      {
        key: "credit",
        label: "Credit",
        render: (row) => (
            <FormFieldSet
              noTitle
              type="text"
              value={row?.credit === "-" ? "" : row?.credit}
              onChange={(value) =>
                handleChange(`entries[${row?._index}].credit`, value)
              }
              placeholder="Credit"
            />
        ),
      },
      {
        key: "attachments",
        label: "Attachments",
        render: (row) => {
          const handleAttachmentChange = (value) => {
            // Filter out duplicates by name
            const existingNames = row.attachments?.filter(a => typeof a === 'string').map(a => a) || [];
            const newFiles = value.filter(file => {
              if (file instanceof File) {
                return !existingNames.includes(file.name) && !row.attachments?.some(a => a instanceof File && a.name === file.name);
              }
              return true;
            });
            const combined = [...(row.attachments || []), ...newFiles];
            handleChange(`entries[${row?._index}].attachments`, combined);
          };

          return (
            <div>
              <FormFieldSet
                noTitle
                type="file"
                onChange={handleAttachmentChange}
                placeholder="Attachments"
                multiple
              />
              {(mode === "edit" || row?.attachments?.length > 0) && (
                <div
                  className="flex rounded-md items-center cursor-pointer"
                  onClick={() => {
                    setSelectedIndex(row?._index);
                    setAttachmentModal(true);
                  }}
                >
                  <Chip
                    label={`Attachments: ${row?.attachments?.length || 0}`}
                    icon={"docs"}
                    iconPosition={"before"}
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: "employee",
        label: "Employee",
        render: (row) => (
            <FormFieldSet
              noTitle
              type="employeesSorted"
              showSearch
              value={row?.employee?.label || row?.employee || ""}
              // value={row?.employee?.label || ""}
              onChange={(value) =>
                handleChange(`entries[${row?._index}].employee`, value)
              }
              keyname="employeesSorted"
              placeholder="Select Employee"
            />
        ),
      },
      {
        key: "exchangeRate",
        label: "Exchange Rate",
        render: (row) => (
            <FormFieldSet
              noTitle
              type="text"
              value={row?.exchangeRate === "-" ? "" : row?.exchangeRate}
              onChange={(value) =>
                handleChange(`entries[${row?._index}].exchangeRate`, value)
              }
              placeholder="Rate"
            />
        ),
      },
      {
        key: "dollarAmount",
        label: "Dollar Amount",
        render: (row) => (
            <FormFieldSet
              noTitle
              type="text"
              value={row?.dollarAmount === "-" ? "" : row?.dollarAmount}
              onChange={(value) =>
                handleChange(`entries[${row?._index}].dollarAmount`, value)
              }
              placeholder="Dollar Amount"
            />
        ),
      },
      // Actions column — hidden in edit mode
      ...(mode !== "edit"
        ? [
            {
              key: "actions",
              label: "Actions",
              render: (row) => (
                <div className="flex gap-2 items-center justify-center">
                    <Button
                      type="icon"
                      icon="add"
                      size="xs"
                      color="black"
                      onClick={addRow}
                      className="p-3"
                    />
                  {formData?.entries?.length > 1 && (
                      <Button
                        type="icon"
                        icon="remove"
                        size="xs"
                        color="danger"
                        onClick={() => removeRow(row?.id)}
                        className="p-3"
                      />
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [
      mode,
      formData,
      employeeOptions,
      addRow,
      removeRow,
      setSelectedIndex,
      setAttachmentModal,
    ],
  );

  const entriesWithIndex = useMemo(
    () =>
      formData?.entries?.map((entry, index) => ({ ...entry, _index: index })),
    [formData?.entries],
  );
  const mappedRows = useMemo(
    () => mapTableRows(entriesWithIndex, columns),
    [entriesWithIndex, columns],
  );

  return (
    <div className="px-6 h-full flex flex-col w-full">
      <div className="flex justify-between items-center mb-6">
        <PageHeader
          Section={
            <PageTitle
              title={"Journal Entries"}
              tag="h5"
              size={"sm"}
              className="text-primary-500 font-semibold"
            />
          }
        />
        <Button
          size="sm"
          title="Back"
          icon="arrow_left_alt"
          onClick={() => {
            handleReset();
            onBack();
          }}
        />
      </div>

      <TableAdv
        tableId={mode === "create" ? "banking_journalentry_create" : "banking_journalentry_edit"}
        rows={mappedRows}
        columns={columns}
      />

      <div className="flex justify-end gap-3 mt-auto pt-6">
        <Button
          title="Cancel"
          variant="outlined"
          onClick={() => {
            handleReset();
            onBack();
          }}
          disabled={loading || submitting}
        />
        <Button
          title={"Save & Close"}
          onClick={handleSave}
          disabled={loading || submitting}
        />
      </div>
      <Modal
        isOpen={attachmentModal}
        onClose={() => setAttachmentModal(false)}
        width="max-w-2xl"
        title={"Manage Attachments"}
      >
        <div className="flex flex-col gap-3 max-h-[400px] overflow-auto">
          {existingAttachments.length === 0 && newAttachments.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-6">
              No attachments available
            </div>
          ) : (
            <>
              {existingAttachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Existing Attachments</h4>
                  <DocumentsList data={existingAttachmentDocs} />
                </div>
              )}
              {newAttachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">New Attachments</h4>
                  {newAttachments.map((file, i) => (
                    <div
                      key={`new-${i}`}
                      className="flex items-center justify-between border rounded-lg px-3 py-2 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="material-icons text-gray-500 text-lg">
                          description
                        </span>
                        <span className="text-sm text-gray-700 truncate max-w-[200px]">
                          {file.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="icon"
                          icon="visibility"
                          size="md"
                          color="black"
                          variant="text"
                          onClick={() => {
                            const url = URL.createObjectURL(file);
                            window.open(url);
                          }}
                        />
                        <Button
                          type="icon"
                          icon="delete"
                          size="md"
                          color="danger"
                          variant="text"
                          onClick={() => handleRemoveAttachment(i, true)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default JournalEntry;

// import React, { useState } from "react";
// import {
//   Button,
//   Chip,
//   FormFieldSet,
//   Modal,
//   PageHeader,
//   PageTitle,
//   useFormHandler,
// } from "../../../../components";
// import { v4 as uuidv4 } from "uuid";
// import { useNavigate, useParams, useLocation } from "react-router-dom";

// const getInitialFormData = (mode, editData) => {
//   if (mode === "edit" && editData) {
//     return {
//       entries: [
//         {
//           id: editData?.id || uuidv4(),
//           date: editData?.date || "",
//           journalNo: editData?.journalId || "",
//           description: editData?.transactionDetails || "",
//           debit: editData?.debit || "",
//           credit: editData?.credit || "",
//           attachments: editData?.attachments || [],
//           employee: editData?.employee || "",
//           exchangeRate: editData?.exchangeRate || "",
//           dollarAmount: editData?.dollarAmount || "",
//         },
//       ],
//     };
//   }

//   // CREATE MODE
//   return {
//     entries: [
//       {
//         id: uuidv4(),
//         date: "",
//         journalNo: "",
//         description: "",
//         debit: "",
//         credit: "",
//         attachments: [],
//         employee: "",
//         exchangeRate: "",
//         dollarAmount: "",
//       },
//       {
//         id: uuidv4(),
//         date: "",
//         journalNo: "",
//         description: "",
//         debit: "",
//         credit: "",
//         attachments: [],
//         employee: "",
//         exchangeRate: "",
//         dollarAmount: "",
//       },
//     ],
//   };
// };

// const JournalEntry = () => {
//   const navigate = useNavigate();
//   const { journalId } = useParams();
//   const { state } = useLocation();
//   console.log(state);

//   const mode = journalId ? "edit" : "create";
//   const editData = state?.data ?? null;
//   const onBack = () => navigate("/banking");

//   const gridCols = mode === "edit" ? "grid-cols-11" : "grid-cols-12";
//   const [initialData] = useState(() => getInitialFormData(mode, editData));
//   const [attachmentModal, setAttachmentModal] = useState(false);
//   const [selectedIndex, setSelectedIndex] = useState(null);

//   const { formData, handleChange, setFormData } = useFormHandler(
//     initialData,
//     {},
//   );

//   const addRow = () => {
//     const newRow = {
//       id: uuidv4(),
//       date: "",
//       journalNo: "",
//       description: "",
//       debit: "",
//       credit: "",
//       attachments: [],
//       employee: "",
//       exchangeRate: "",
//       dollarAmount: "",
//     };

//     setFormData({
//       ...formData,
//       entries: [...formData?.entries, newRow],
//     });
//   };

//   const removeRow = (id) => {
//     setFormData({
//       ...formData,
//       entries: formData?.entries?.filter((row) => row?.id !== id),
//     });
//   };

//   const duplicateRow = (index) => {
//     const currentRow = formData?.entries[index];

//     const duplicatedRow = {
//       ...currentRow,
//       id: uuidv4(),
//     };

//     const updatedEntries = [...formData?.entries];
//     updatedEntries.splice(index + 1, 0, duplicatedRow);

//     setFormData({
//       ...formData,
//       entries: updatedEntries,
//     });
//   };

//   const handleReset = () => {
//     setFormData(getInitialFormData(mode, editData));
//   };

//   const handleSave = () => {
//     console.log("Final Data:", formData);
//     handleReset();
//     onBack();
//   };

//   const employeeOptions = [
//     { label: "Raghu", value: "raghu" },
//     { label: "Aishwarya Vedula", value: "Aishwarya Vedula" },
//     { label: "Abdul Waseem Syed", value: "Abdul Waseem Syed" },
//     { label: "Amol Dasani", value: "Amol Dasani" },
//   ];

//   const combineAttachments =
//     formData?.entries?.[selectedIndex]?.attachments?.map((file) => ({
//       file,
//       name: file?.name || (typeof file === "string" ? file : "Attachment"),
//     })) || [];

//   return (
//     <div className="px-6 h-full flex flex-col w-full">
//       {/* Header */}
//       <div className="flex justify-between items-center mb-6">
//         <PageHeader
//           Section={
//             <PageTitle
//               title={"Journal Entries"}
//               tag="h5"
//               size={"sm"}
//               className="text-primary-500 font-semibold"
//             />
//           }
//         />

//         <Button
//           size="sm"
//           title="Back"
//           icon="arrow_left_alt"
//           onClick={() => {
//             handleReset();
//             onBack();
//           }}
//         />
//       </div>

//       {/* Table */}
//       <div className="overflow-x-auto">
//         <div className="min-w-[2500px] pb-3">
//           {/* Header */}
//           <div
//             className={`grid ${gridCols} gap-3 text-sm font-medium text-gray-600 border-b p-2 bg-primary-50`}
//           >
//             <div>Journal No</div>
//             <div>Date</div>
//             <div className="col-span-2">Description</div>
//             <div>Debit</div>
//             <div>Credit</div>
//             <div className="col-span-2">Attachments</div>
//             <div>Employee</div>
//             <div>Exchange Rate</div>
//             <div>Dollar Amount</div>
//             {mode !== "edit" && <div className="text-center">Actions</div>}
//           </div>

//           {/* Rows */}
//           <div className="flex flex-col gap-3 mt-3">
//             {formData?.entries?.map((entry, index) => (
//               <div key={entry?.id} className={`grid ${gridCols} gap-3`}>
//                 <FormFieldSet
//                   noTitle
//                   type="text"
//                   title="Journal No"
//                   placeholder="Journal No"
//                   value={entry?.journalNo}
//                   onChange={(value) =>
//                     handleChange(`entries[${index}].journalNo`, value)
//                   }
//                 />
//                 <FormFieldSet
//                   noTitle
//                   type="datepicker"
//                   value={entry?.date}
//                   onChange={(value) =>
//                     handleChange(`entries[${index}].date`, value)
//                   }
//                   placeholder="Select Date"
//                   showDropdown={false}
//                 />
//                 <div className="col-span-2 pl-5">
//                   <FormFieldSet
//                     noTitle
//                     type="text"
//                     value={entry?.description}
//                     onChange={(value) =>
//                       handleChange(`entries[${index}].description`, value)
//                     }
//                     placeholder="Description"
//                   />
//                 </div>

//                 <FormFieldSet
//                   noTitle
//                   type="text"
//                   value={entry?.debit}
//                   onChange={(value) =>
//                     handleChange(`entries[${index}].debit`, value)
//                   }
//                   placeholder="Debit"
//                 />

//                 <FormFieldSet
//                   noTitle
//                   type="text"
//                   value={entry?.credit}
//                   onChange={(value) =>
//                     handleChange(`entries[${index}].credit`, value)
//                   }
//                   placeholder="Crebit"
//                 />
//                 <div className="col-span-2">
//                   <FormFieldSet
//                     noTitle
//                     type="file"
//                     onChange={(value) =>
//                       handleChange(`entries[${index}].attachments`, value)
//                     }
//                     placeholder="Attachments"
//                     multiple
//                   />
//                   {mode === "edit" && (
//                     <div
//                       className="flex rounded-md items-center cursor-pointer"
//                       onClick={() => {
//                         setSelectedIndex(index);
//                         setAttachmentModal(true);
//                       }}
//                     >
//                       <Chip
//                         label={`Attachments: ${
//                           entry?.attachments?.length || 0
//                         }`}
//                         icon={"docs"}
//                         iconPosition={"before"}
//                       />
//                     </div>
//                   )}
//                 </div>
//                 <FormFieldSet
//                   noTitle
//                   type="select"
//                   options={employeeOptions}
//                   value={entry?.employee}
//                   onChange={(value) =>
//                     handleChange(`entries[${index}].employee`, value)
//                   }
//                   placeholder="Employee"
//                 />
//                 <FormFieldSet
//                   noTitle
//                   type="text"
//                   value={entry?.exchangeRate}
//                   onChange={(value) =>
//                     handleChange(`entries[${index}].exchangeRate`, value)
//                   }
//                   placeholder="Rate"
//                 />

//                 <FormFieldSet
//                   noTitle
//                   type="text"
//                   value={entry?.dollarAmount}
//                   onChange={(value) =>
//                     handleChange(`entries[${index}].dollarAmount`, value)
//                   }
//                   placeholder="Dollar Amount"
//                 />
//                 {mode !== "edit" && (
//                   <div className="flex gap-2 items-center justify-center">
//                     <Button
//                       type="icon"
//                       icon="add"
//                       size="xs"
//                       color="black"
//                       onClick={addRow}
//                       className="p-3"
//                     />
//                     {formData?.entries?.length > 1 && (
//                       <Button
//                         type="icon"
//                         icon="remove"
//                         size="xs"
//                         color="danger"
//                         onClick={() => removeRow(entry?.id)}
//                         className="p-3"
//                       />
//                     )}
//                   </div>
//                 )}
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* Footer */}
//       <div className="flex justify-end gap-3 mt-auto pt-6">
//         <Button
//           title="Cancel"
//           variant="outlined"
//           onClick={() => {
//             handleReset();
//             onBack();
//           }}
//         />
//         <Button title={"Save & Close"} onClick={handleSave} />
//       </div>

//       <Modal
//         isOpen={attachmentModal}
//         onClose={() => setAttachmentModal(false)}
//         width="max-w-2xl"
//         title={"Document Details"}
//       >
//         <div className="flex flex-col gap-3 max-h-[400px] overflow-auto">
//           {combineAttachments.length === 0 ? (
//             <div className="text-center text-gray-400 text-sm py-6">
//               No attachments available
//             </div>
//           ) : (
//             combineAttachments.map((item, i) => (
//               <div
//                 key={i}
//                 className="flex items-center justify-between border rounded-lg px-3 py-2 hover:bg-gray-50"
//               >
//                 {/* Left */}
//                 <div className="flex items-center gap-2">
//                   <span className="material-icons text-gray-500 text-lg">
//                     description
//                   </span>
//                   <span className="text-sm text-gray-700 truncate max-w-[200px]">
//                     {item.name}
//                   </span>
//                 </div>

//                 {/* Right Actions */}
//                 <div className="flex items-center gap-2">
//                   {item.file && typeof item.file !== "string" && (
//                     <Button
//                       type="icon"
//                       icon="visibility"
//                       size="md"
//                       color="black"
//                       variant="text"
//                       onClick={() => {
//                         const url = URL.createObjectURL(item.file);
//                         window.open(url);
//                       }}
//                     />
//                   )}

//                   <Button
//                     type="icon"
//                     icon="download"
//                     size="md"
//                     color="black"
//                     variant="text"
//                     onClick={() => {
//                       if (typeof item.file === "string") {
//                         window.open(item.file);
//                       } else {
//                         const url = URL.createObjectURL(item.file);
//                         const a = document.createElement("a");
//                         a.href = url;
//                         a.download = item.name;
//                         a.click();
//                       }
//                     }}
//                   />
//                 </div>
//               </div>
//             ))
//           )}
//         </div>
//       </Modal>
//     </div>
//   );
// };

// export default JournalEntry;
